<script setup>
/**
 * The "Complete setup" page: a guided journey through the five workspace
 * setup steps (design variant B). A timeline on the left selects a step; the
 * detail panel on the right stages it with its own call to action. Steps
 * derive from workspace state (see useSetupSteps), so finishing a step
 * elsewhere completes it here on the next visit.
 */
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useSetupSteps } from './useSetupSteps';
import { useSupportChatWidget } from 'dashboard/composables/useSupportChatWidget';

import Button from 'dashboard/components-next/button/Button.vue';

const { t } = useI18n();
const { steps, doneCount, fetchSetupData } = useSetupSteps();
const { isAvailable: canChatWithSupport, toggleSupportChat } =
  useSupportChatWidget();

onMounted(fetchSetupData);

// The selected step defaults to the first unfinished one; a timeline click
// overrides it, and "Skip" moves to the next unfinished step after it.
const selectedKey = ref(null);
const firstOpenKey = computed(
  () => steps.value.find(step => !step.done)?.key ?? steps.value[0].key
);
watch(firstOpenKey, key => {
  if (!selectedKey.value) selectedKey.value = key;
});

const selectedStep = computed(
  () =>
    steps.value.find(step => step.key === selectedKey.value) ??
    steps.value.find(step => step.key === firstOpenKey.value)
);

const skipStep = () => {
  const list = steps.value;
  const index = list.findIndex(step => step.key === selectedStep.value.key);
  const next =
    list.slice(index + 1).find(step => !step.done) ||
    list.find(step => !step.done && step.key !== selectedStep.value.key);
  if (next) selectedKey.value = next.key;
};

const helperCards = computed(() =>
  [
    canChatWithSupport.value && {
      key: 'support',
      icon: 'i-ph-chat-circle-dots',
      title: t('SETUP.HELPERS.SUPPORT.TITLE'),
      description: t('SETUP.HELPERS.SUPPORT.DESCRIPTION'),
      action: toggleSupportChat,
    },
    {
      key: 'campaigns',
      icon: 'i-ph-megaphone',
      title: t('SETUP.HELPERS.CAMPAIGNS.TITLE'),
      description: t('SETUP.HELPERS.CAMPAIGNS.DESCRIPTION'),
      to: { name: 'campaigns_home' },
    },
    {
      key: 'widget',
      icon: 'i-ph-paint-brush-broad',
      title: t('SETUP.HELPERS.WIDGET.TITLE'),
      description: t('SETUP.HELPERS.WIDGET.DESCRIPTION'),
      to: { name: 'settings_inbox_list' },
    },
  ].filter(Boolean)
);
</script>

<template>
  <div class="flex-1 overflow-y-auto bg-n-surface-1">
    <div class="mx-auto flex max-w-4xl flex-col gap-8 px-6 py-10">
      <header class="flex items-end gap-6">
        <span
          class="font-interDisplay text-7xl font-semibold leading-none tracking-tight text-n-violet-9"
          aria-hidden="true"
        >
          {{ doneCount }}/{{ steps.length }}
        </span>
        <div class="min-w-0 pb-1">
          <h1
            class="m-0 font-interDisplay text-2xl font-semibold tracking-tight text-n-slate-12"
          >
            {{ t('SETUP.TITLE') }}
          </h1>
          <p class="m-0 mt-1 text-sm text-n-slate-11">
            {{ t('SETUP.SUBTITLE') }}
          </p>
        </div>
      </header>

      <div class="grid gap-6 min-[880px]:grid-cols-[280px_1fr]">
        <!-- Timeline -->
        <ol class="m-0 flex list-none flex-col p-0">
          <li v-for="(step, index) in steps" :key="step.key" class="relative">
            <span
              v-if="index < steps.length - 1"
              aria-hidden="true"
              class="absolute ltr:left-[15px] rtl:right-[15px] top-9 h-[calc(100%-1.75rem)] w-px"
              :class="step.done ? 'bg-n-teal-8' : 'bg-n-weak'"
            />
            <button
              type="button"
              class="relative flex w-full items-center gap-3 rounded-lg px-2 py-2.5 text-left rtl:text-right outline outline-1 outline-transparent hover:bg-n-alpha-1 focus-visible:outline-2 focus-visible:outline-n-violet-9"
              :class="{
                'bg-n-solid-1 shadow-sm outline-n-weak':
                  step.key === selectedStep.key,
              }"
              :aria-current="step.key === selectedStep.key ? 'step' : undefined"
              @click="selectedKey = step.key"
            >
              <span
                class="grid size-8 flex-shrink-0 place-items-center rounded-full"
                :class="
                  step.done
                    ? 'bg-n-teal-3 text-n-teal-11'
                    : 'bg-n-alpha-2 text-n-slate-11'
                "
              >
                <span
                  :class="step.done ? 'i-ph-check-bold' : 'i-ph-circle-dashed'"
                  class="size-4"
                />
              </span>
              <span
                class="min-w-0 truncate text-sm"
                :class="
                  step.done
                    ? 'text-n-slate-10 line-through'
                    : 'font-medium text-n-slate-12'
                "
              >
                {{ step.title }}
              </span>
            </button>
          </li>
        </ol>

        <!-- Detail panel -->
        <section
          class="flex flex-col gap-4 rounded-xl border border-n-weak bg-n-solid-1 p-6"
        >
          <div
            class="w-fit rounded-full px-3 py-1 text-xs font-medium"
            :class="
              selectedStep.done
                ? 'bg-n-teal-3 text-n-teal-11'
                : 'bg-n-violet-3 text-n-violet-11'
            "
          >
            {{
              selectedStep.done ? t('SETUP.STATE.DONE') : t('SETUP.STATE.OPEN')
            }}
          </div>
          <h2
            class="m-0 font-interDisplay text-xl font-semibold tracking-tight text-n-slate-12"
          >
            {{ selectedStep.title }}
          </h2>
          <p class="m-0 text-sm leading-6 text-n-slate-11">
            {{ selectedStep.description }}
          </p>
          <div v-if="!selectedStep.done" class="mt-2 flex items-center gap-3">
            <router-link :to="selectedStep.to">
              <Button :label="selectedStep.cta" color="violet" size="sm" />
            </router-link>
            <button
              type="button"
              class="text-sm text-n-slate-11 hover:text-n-slate-12"
              @click="skipStep"
            >
              {{ t('SETUP.SKIP') }}
            </button>
          </div>
        </section>
      </div>

      <!-- Helper cards -->
      <div class="grid gap-4 min-[880px]:grid-cols-3">
        <component
          :is="card.to ? 'router-link' : 'button'"
          v-for="card in helperCards"
          :key="card.key"
          :to="card.to"
          type="button"
          class="flex flex-col items-start gap-2 rounded-xl border border-n-weak bg-n-solid-1 p-4 text-left rtl:text-right outline outline-1 outline-transparent hover:border-n-strong focus-visible:outline-2 focus-visible:outline-n-violet-9"
          @click="card.action && card.action()"
        >
          <span
            class="grid size-9 place-items-center rounded-lg bg-n-violet-3 text-n-violet-11"
          >
            <span :class="card.icon" class="size-5" />
          </span>
          <span class="text-sm font-medium text-n-slate-12">
            {{ card.title }}
          </span>
          <span class="text-xs leading-5 text-n-slate-11">
            {{ card.description }}
          </span>
        </component>
      </div>
    </div>
  </div>
</template>
